import {
  Button,
  Card,
  Container,
  makeStyles,
  Table,
  TableHead,
  TableRow,
  Typography
} from "@material-ui/core";
import { ArrowDownward } from "@material-ui/icons";
import { ConfirmButtonTransitionState } from "@saleor/components/ConfirmButton";
import PageBackNavigation from "@saleor/components/PageBackNavigation";
import PageBreadcrumbNavigation from "@saleor/components/PageBreadcrumbNavigation";
import PageHeader from "@saleor/components/PageHeader";
import Skeleton from "@saleor/components/Skeleton";
import TableCellHeader from "@saleor/components/TableCellHeader";
import { Timeline } from "@saleor/components/Timeline";
import { TimelineNewValues } from "@saleor/components/Timeline/TimelineNewValues";
import TimelineOldValues from "@saleor/components/Timeline/TimelineOldValues";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import { sectionNames } from "@saleor/intl";
import { useGenerateGroupLogs } from "@saleor/mfa/mutations";
import { maybe } from "@saleor/misc";
import { getMoment } from "@saleor/motherOrders/views/OrderList/handlers";
import {
  useExportGroupLogsFile,
  useGroupActivityLogsQuery
} from "@saleor/permissionGroups/queries";
import { permissionGroupListUrl } from "@saleor/permissionGroups/urls";
import { ActivityLogsNewValues } from "@saleor/permissionGroups/utils/activityLogsNewValues";
import { parseNewValues } from "@saleor/permissionGroups/utils/utils";
import {
  ExportScope,
  FileTypesEnum,
  JobStatusEnum
} from "@saleor/types/globalTypes";
import { getGteLteVariables } from "@saleor/utils/filters";
import React, { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

const useStyles = makeStyles(
  () => ({
    templateTitle: {
      color: "#000",
      fontSize: "17px",
      fontWeight: 600,
      margin: "20px 0 20px"
    }
  }),
  { name: "PermissionGroupMultiFactorAuthentication" }
);

const PermissionGroupMultiFactorAuthentication: React.FC<any> = props => {
  const intl = useIntl();
  const classes = useStyles(props);
  const notify = useNotifier();
  const navigate = useNavigator();

  const today = getMoment("YYYY-MM-DD");
  const month = getMoment("YYYY-MM-DD", -31);

  const MAX_DOWNLOAD_RETRIES = 5;

  const [exportGroupLogsFileID, setExportGroupLogsFileID] = useState<
    string | undefined
  >();
  const [downloadStatus, setDownloadStatus] = useState<
    ConfirmButtonTransitionState
  >("default");
  const [downloadRequestCtr, setDownloadRequestCtr] = React.useState(0);

  const handleConfirm = async () => {
    setExportGroupLogsFileID(null);
    setDownloadStatus("loading");

    const result = await generateGroupLogs({
      variables: {
        input: {
          fileName: `MFA Group Activity Logs${getMoment("_YYYYMMDDHHmmss")}`,
          fileType: FileTypesEnum.CSV,
          filter: {
            created: getGteLteVariables({ gte: month, lte: today })
          },
          scope: ExportScope.FILTER
        }
      }
    });

    return result.data.exportGroupLogs.errors;
  };

  const [generateGroupLogs] = useGenerateGroupLogs({
    onCompleted: ({ exportGroupLogs }) => {
      if (exportGroupLogs?.errors?.length === 0) {
        setExportGroupLogsFileID(exportGroupLogs?.exportFile?.id);
        setDownloadRequestCtr(0);
      } else {
        setDownloadStatus("error");
        setExportGroupLogsFileID(null);
        notify({
          status: "error",
          text: intl.formatMessage({
            defaultMessage: "Failed to generate logs."
          })
        });
      }
    },
    onError: errors => {
      const [error] = errors.graphQLErrors;
      setDownloadStatus("error");
      setExportGroupLogsFileID(null);
      notify({
        status: "error",
        text: error?.message
      });
    }
  });

  const {
    data: exportGroupLogsFileData,
    refetch: refetchExportGroupLogsFile
  } = useExportGroupLogsFile({
    displayLoader: true,
    skip: !!(
      exportGroupLogsFileID === null ||
      exportGroupLogsFileID === undefined ||
      exportGroupLogsFileID === ""
    ),
    variables: { id: exportGroupLogsFileID }
  });

  React.useEffect(() => {
    if (downloadRequestCtr > MAX_DOWNLOAD_RETRIES) {
      notify({
        status: "error",
        text: intl.formatMessage({
          defaultMessage: "Download failed after multiple attempts."
        })
      });
      setDownloadStatus("error");
      return;
    }
    if (exportGroupLogsFileData?.exportFile !== undefined) {
      if (
        exportGroupLogsFileData?.exportFile?.status === JobStatusEnum.SUCCESS
      ) {
        window.open(
          exportGroupLogsFileData?.exportFile?.url,
          "_blank",
          "noopener,noreferrer"
        );
        setDownloadStatus("success");
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "Successfully downloaded."
          })
        });
      } else if (
        exportGroupLogsFileData?.exportFile?.status === JobStatusEnum.FAILED
      ) {
        notify({
          status: "error",
          text: intl.formatMessage({
            defaultMessage: "Download Failed"
          })
        });
        setDownloadStatus("error");
      } else {
        setDownloadRequestCtr(downloadRequestCtr + 1);
        setDownloadStatus("loading");
        refetchExportGroupLogsFile();
      }
    }
  }, [exportGroupLogsFileData]);

  const breadcrumbDirectory = [
    { name: "User Types", url: "/permission-groups" },
    { name: "Multi-Factor Authentication - Activity Logs" }
  ];

  const { data } = useGroupActivityLogsQuery({
    displayLoader: true,
    variables: { first: 100 }
  });

  const events = maybe(() => data.groupActivityLogs.edges);

  return (
    <Container>
      <PageBreadcrumbNavigation items={breadcrumbDirectory} />
      <div style={{ display: "flex", flexDirection: "row", marginTop: "15px" }}>
        <PageBackNavigation onBack={() => navigate(permissionGroupListUrl())} />
        <PageHeader
          title={intl.formatMessage(sectionNames.multiFactorAuthentication)}
        />
      </div>
      <Card>
        <Table>
          <TableHead>
            <TableRow>
              <TableCellHeader>
                <span style={{ fontSize: "18px", fontWeight: "bold" }}>
                  <FormattedMessage
                    defaultMessage="Multi-Factor Authentication Activities"
                    description="Multi-Factor Authentication Activities"
                  />
                </span>
              </TableCellHeader>
              <TableCellHeader textAlign="right">
                <Button
                  color="default"
                  variant="contained"
                  disabled={downloadStatus === "loading"}
                  style={{ backgroundColor: "#F4F8FF", color: "#1848FE" }}
                  onClick={() => handleConfirm()}
                  startIcon={
                    downloadStatus === "loading" ? "" : <ArrowDownward />
                  }
                >
                  {downloadStatus === "loading"
                    ? "Exporting..."
                    : "Export as CSV"}
                </Button>
              </TableCellHeader>
            </TableRow>
          </TableHead>
        </Table>
        <Container>
          {events ? (
            (() => {
              const groupedEvents: Record<string, typeof events> = {};
              events
                .slice()
                .reverse()
                .forEach(event => {
                  const fullDate = event.node?.updatedAt;
                  const dateObj = new Date(fullDate);
                  const formattedDate = dateObj.toLocaleDateString("en-US", {
                    day: "numeric",
                    month: "long",
                    year: "numeric"
                  });
                  if (!groupedEvents[formattedDate]) {
                    groupedEvents[formattedDate] = [];
                  }
                  groupedEvents[formattedDate].push(event);
                });
              return Object.entries(groupedEvents).map(([date, dateEvents]) => (
                <div key={date}>
                  <Typography className={classes.templateTitle}>
                    {date}
                  </Typography>
                  <Timeline>
                    {dateEvents.map(event => {
                      const fullDate = event.node?.updatedAt;
                      const parsedNewValues = parseNewValues(
                        event.node.newValues
                      );
                      const parsedOldValues = parseNewValues(
                        event.node.oldValues
                      );
                      const timelineProps = {
                        date: fullDate,
                        newValues: parsedNewValues as ActivityLogsNewValues,
                        user: event.node?.user
                      };
                      if (parsedOldValues === parsedNewValues) {
                        return (
                          <TimelineNewValues
                            key={event.node.id}
                            {...timelineProps}
                          />
                        );
                      } else {
                        return (
                          <TimelineOldValues
                            key={event.node.id}
                            {...timelineProps}
                            oldValues={parsedOldValues as ActivityLogsNewValues}
                          />
                        );
                      }
                    })}
                  </Timeline>
                </div>
              ));
            })()
          ) : (
            <Skeleton />
          )}
        </Container>
      </Card>
    </Container>
  );
};

export default PermissionGroupMultiFactorAuthentication;
